/**
 * Service d'upload de fichiers volumineux vers Firebase Storage
 * et génération de liens de téléchargement
 */

// Configuration pour upload sur serveur local
const UPLOAD_CONFIG = {
    uploadEndpoint: 'https://v2.sojoclient.com/backend/main.php/upload-file', // Endpoint d'upload
    downloadBaseUrl: 'https://v2.sojoclient.com/backend/reports/', // Base URL pour téléchargement
    maxFileSize: 100 * 1024 * 1024, // 100MB max
    allowedTypes: ['application/pdf', 'image/jpeg', 'image/png'],
    folderPath: 'reports/'
};

/**
 * Upload un fichier vers le service en ligne
 * @param {File|Blob} file - Le fichier à uploader
 * @param {string} fileName - Nom du fichier
 * @param {string} fileType - Type MIME du fichier
 * @returns {Promise<{success: boolean, fileId?: string, downloadUrl?: string, error?: string}>}
 */
async function uploadFile(file, fileName, fileType) {
    try {
        // Validation de la taille
        if (file.size > UPLOAD_CONFIG.maxFileSize) {
            return {
                success: false,
                error: `Fichier trop volumineux. Taille max: ${UPLOAD_CONFIG.maxFileSize / (1024 * 1024)}MB`
            };
        }

        // Validation du type
        if (!UPLOAD_CONFIG.allowedTypes.includes(fileType)) {
            return {
                success: false,
                error: `Type de fichier non autorisé: ${fileType}`
            };
        }

        // Générer un ID unique pour le fichier
        const fileId = generateFileId();
        
        // Upload vers serveur local
        console.log(`🔄 Upload en cours: ${fileName} (${(file.size / 1024).toFixed(2)} KB)`);
        
        // Upload vers serveur
        const downloadUrl = await uploadToServer(file, fileId, fileName);
        
        console.log(`✅ Upload terminé: ${fileName}`);
        console.log(`📎 Lien de téléchargement: ${downloadUrl}`);
        
        return {
            success: true,
            fileId: fileId,
            downloadUrl: downloadUrl,
            fileName: fileName,
            fileSize: file.size,
            expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 jours
        };
        
    } catch (error) {
        console.error('Erreur lors de l\'upload:', error);
        return {
            success: false,
            error: `Erreur d'upload: ${error.message}`
        };
    }
}

/**
 * Upload un fichier à partir de contenu base64
 * @param {string} base64Content - Contenu du fichier en base64
 * @param {string} fileName - Nom du fichier
 * @param {string} fileType - Type MIME du fichier
 * @returns {Promise<{success: boolean, fileId?: string, downloadUrl?: string, error?: string}>}
 */
async function uploadFromBase64(base64Content, fileName, fileType) {
    try {
        // Nettoyer le base64
        const cleanBase64 = base64Content.replace(/^data:[^;]+;base64,/, '');
        
        // Convertir en Blob
        const binaryString = atob(cleanBase64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: fileType });
        
        return await uploadFile(blob, fileName, fileType);
        
    } catch (error) {
        console.error('Erreur lors de la conversion base64:', error);
        return {
            success: false,
            error: `Erreur de conversion base64: ${error.message}`
        };
    }
}

/**
 * Génère un ID unique pour le fichier
 * @returns {string}
 */
function generateFileId() {
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 15);
    return `${timestamp}_${randomStr}`;
}

/**
 * Génère l'URL de téléchargement
 * @param {string} fileId - ID du fichier
 * @param {string} fileName - Nom du fichier original
 * @returns {string}
 */
function generateDownloadUrl(fileId, fileName) {
    const encodedFileName = encodeURIComponent(fileName);
    const expiryTime = Date.now() + (7 * 24 * 60 * 60 * 1000); // 7 jours
    return `${UPLOAD_CONFIG.downloadBaseUrl}${fileId}/${encodedFileName}?expires=${expiryTime}`;
}

/**
 * Upload vers le serveur local
 * @param {Blob} file - Le fichier
 * @param {string} fileId - ID du fichier
 * @param {string} fileName - Nom original du fichier
 * @returns {Promise<string>} URL de téléchargement
 */
async function uploadToServer(file, fileId, fileName) {
    console.log('Uploading to server:', {
        fileSize: file.size,
        fileName: fileName,
        fileId: fileId
    });
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileId', fileId);
    formData.append('fileName', fileName);
    
    // Obtenir le token d'authentification
    const token = localStorage.getItem('token');
    if (!token) {
        throw new Error('Authentication token required');
    }
    
    try {
        console.log('Starting server upload...');
        
        // Créer un AbortController avec timeout de 8 minutes
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 480000); // 8 minutes
        
        const response = await fetch(UPLOAD_CONFIG.uploadEndpoint, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData,
            signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
            const errorText = await response.text();
            console.error('Server upload error:', errorText);
            throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
        }
        
        const result = await response.json();
        console.log('Server upload completed:', result);
        
        if (result.status === 'success') {
            const downloadURL = `${UPLOAD_CONFIG.downloadBaseUrl}${result.fileName}`;
            console.log('Server download URL:', downloadURL);
            return downloadURL;
        } else {
            throw new Error(result.message || 'Upload failed');
        }
    } catch (error) {
        console.error('Server upload error:', error);
        
        if (error.name === 'AbortError') {
            throw new Error('Upload timeout: The server took too long to respond. Please try again with a smaller file or check your internet connection.');
        } else if (error.message.includes('Failed to fetch')) {
            throw new Error('Network error: Unable to connect to server. Please check your internet connection.');
        } else {
            throw new Error(`Server upload failed: ${error.message}`);
        }
    }
}

/**
 * Vérifie si un fichier nécessite un upload (taille > seuil)
 * @param {number} fileSize - Taille du fichier en bytes
 * @returns {boolean}
 */
function requiresUpload(fileSize) {
    const threshold = 5 * 1024 * 1024; // 5MB
    return fileSize > threshold;
}

/**
 * Traite un fichier pour l'envoi d'email
 * - Si petit : retourne le contenu base64 pour pièce jointe directe
 * - Si gros : upload vers service et retourne le lien
 * @param {string} base64Content - Contenu en base64
 * @param {string} fileName - Nom du fichier
 * @param {string} fileType - Type MIME
 * @returns {Promise<{method: 'attachment'|'link', content?: string, downloadUrl?: string, error?: string}>}
 */
async function processFileForEmail(base64Content, fileName, fileType) {
    try {
        // Calculer la taille approximative du fichier
        const cleanBase64 = base64Content.replace(/^data:[^;]+;base64,/, '');
        const fileSize = (cleanBase64.length * 3) / 4; // Taille approximative en bytes
        
        console.log(`📁 Traitement du fichier: ${fileName} (${(fileSize / 1024).toFixed(2)} KB)`);
        
        if (requiresUpload(fileSize)) {
            console.log(`📤 Fichier volumineux détecté, upload vers service en ligne...`);
            
            const uploadResult = await uploadFromBase64(base64Content, fileName, fileType);
            
            if (uploadResult.success) {
                return {
                    method: 'link',
                    downloadUrl: uploadResult.downloadUrl,
                    fileName: fileName,
                    fileSize: fileSize,
                    expiresAt: uploadResult.expiresAt
                };
            } else {
                return {
                    error: uploadResult.error
                };
            }
        } else {
            console.log(`📎 Fichier de taille normale, envoi en pièce jointe directe`);
            return {
                method: 'attachment',
                content: cleanBase64,
                fileName: fileName,
                fileType: fileType
            };
        }
        
    } catch (error) {
        console.error('Erreur lors du traitement du fichier:', error);
        return {
            error: `Erreur de traitement: ${error.message}`
        };
    }
}

// Export des fonctions pour utilisation
if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        uploadFile,
        uploadFromBase64,
        processFileForEmail,
        requiresUpload,
        generateDownloadUrl
    };
}

// Pour utilisation dans le navigateur
if (typeof window !== 'undefined') {
    window.FileUploadService = {
        uploadFile,
        uploadFromBase64,
        processFileForEmail,
        requiresUpload,
        generateDownloadUrl
    };
}